import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX } from "react";
import type { DocumentPosition } from "./anchor-map";
import { type HighlightRect, PdfPageCanvas, type PdfDocumentProxy } from "./pdf-page";

/**
 * Bir tarafın BÜTÜN sayfalarını sürekli akış hâlinde çizen panel.
 *
 * <p><b>Neden sürekli akış ve neden sayfalama DEĞİL.</b> Bir ara sürüm tek sayfa gösterip
 * ileri/geri düğmeleri koydu. Yanlıştı: eşzamanlı kaydırmanın bütün anlamı iki belgeyi
 * AYNI ANDA aynı yerden okumak ve sayfalama onu "aynı sayfa numarasında mıyız" sorusuna
 * indiriyor — karşılaştırma aletinin verdiği hissi öldürüyor. İki belge yan yana akar.</p>
 *
 * <p><b>Asıl hata pencerelemedeydi.</b> Önceki sürekli akış sürümü yalnızca "görünen sayfa
 * ±1" çiziyordu (<c>RENDER_WINDOW = 1</c>). Görünür alana ikiden fazla sayfa sığdığında —
 * uzaklaştırma, geniş ekran, küçük sayfa — EKRANDA DURAN sayfalar "sf 2" yer tutucusu
 * olarak kalıyordu. Pencere artık sabit bir sayfa mesafesi değil, GERÇEKTEN GÖRÜNEN aralık:
 * görünür kutuyla kesişen her sayfa çiziliyor, üstüne bir ekran boyu pay ekleniyor.</p>
 *
 * <p><b>Yer tutucu yüksekliği TAHMİN DEĞİL.</b> A4 varsayıldığı sürümde yer tutucu ↔ canvas
 * geçişinde belgenin toplam yüksekliği değişiyor, kaydırma konumu kayıyor ve eşzamanlı
 * kaydırmada iki panel birbirinden ayrı düşüyordu. Her sayfanın gerçek en-boy oranı
 * belgeden bir kez okunuyor; yer tutucu ile canvas aynı yüksekliği kaplıyor.</p>
 *
 * <p><b>Normalize koordinat sözleşmesi korunuyor.</b> Vurgular hâlâ <c>PdfPageCanvas</c>'ın
 * SVG katmanında ve hâlâ 0-1 aralığında; ölçek değişince dikdörtgenler kendiliğinden doğru
 * yerde kalıyor. Tarayıcıda hiçbir pixel dönüşümü tekrarlanmıyor.</p>
 */

/** Oranlar okunana kadar kullanılan geçici sayfa oranı (A4 dikey). Kalıcı varsayım DEĞİL. */
const FALLBACK_PAGE_ASPECT = 297 / 210;

/** Sayfa çevresindeki boşluk (CSS pixel). */
const PAGE_PADDING = 10;

/** Sayfalar arası boşluk. */
const PAGE_GAP = 12;

/**
 * Görünür aralığın dışında kaç pixel daha çizilir.
 *
 * Bir ekran boyu pay: hızlı kaydırmada boş kutu görünmesin. Sayfa SAYISI cinsinden bir pay
 * (eski "±1 sayfa") yanlış birimdi — aynı sayı, sayfa küçükken az sayfa küçükken çok sayfa
 * demek oluyordu ve tam bu yüzden görünür sayfalar çizilmeden kalıyordu.
 */
const OVERSCAN_RATIO = 1;

/**
 * Aynı anda çizilecek en fazla sayfa.
 *
 * Uç bir durumda (çok uzaklaştırılmış, çok sayfalı bir belge) onlarca canvas birden
 * bellekte tutulmasın.
 *
 * <p><b>Bu sınır GÖRÜNÜR sayfaları asla kırpmıyor.</b> Önceki sürüm aralığı baştan
 * sayıyor ve kuyruğu kesiyordu; uzaklaştırıldığında ekrana sığan sayfa sayısı sınırı
 * aştığında kesilen kısım tam da ekranın ALTINDAKİ sayfalar oluyordu ve kullanıcı
 * baktığı sayfada "sf 13" yer tutucusu görüyordu. Bütçe artık yalnızca ÖN YÜKLEME payına
 * uygulanıyor: görünür olan her sayfa her hâlükârda çiziliyor.</p>
 */
const MAX_RENDERED_PAGES = 12;

/**
 * Panelin ebeveyne verdiği imperatif tutamak.
 *
 * <p><b>Neden imperatif.</b> Eşzamanlı kaydırma önceden React state'inden geçiyordu:
 * kaydır → <c>setState</c> → tüm görünüm yeniden çizilir → <c>useLayoutEffect</c> → karşı
 * panelde <c>scrollTop</c>. Kaydırma saniyede onlarca olay üretiyor ve her biri bir render
 * turu demekti; kullanıcının gördüğü "takıla takıla" giden bir takipti. Kaydırma bir
 * animasyondur; React'in render döngüsü onun taşıyıcısı değildir.</p>
 */
export interface DocumentPaneApi {
  /** Verilen mantıksal konuma (sayfa + sayfa içi oran) götürür. Anlık, animasyonsuz. */
  scrollToPosition(position: DocumentPosition): void;
  /** Sayfayı görünür alanın ortasına getirir. Yumuşak — kullanıcı tıklamasının cevabı. */
  scrollToPage(page: number): void;
}

export interface DocumentPaneProps {
  document: PdfDocumentProxy | null;
  /** Bu tarafın sayfa sayısı — tel sözleşmesinden geliyor, PDF'ten değil. */
  pageCount: number;
  /** 1-tabanlı sayfa numarası için o sayfanın vurguları. */
  highlightsFor: (page: number) => HighlightRect[];
  /** Panel başlığı (ör. "Birinci" / "İkinci"). */
  label: string;
  /** Sayfa genişliği (CSS pixel). Zoom bunu değiştiriyor. */
  pageWidth: number;
  /**
   * Panel kaydığında çağrılır. Ebeveyn hangi tarafın SÜRÜCÜ olduğuna karar veriyor ve
   * takipçiden gelen bildirimleri yok sayıyor; sahiplik tek bir yerde durduğu için iki
   * panel birbirini kovalayamıyor.
   */
  onScrollPosition?: (position: DocumentPosition) => void;
  /** Tutamağı ebeveyne verir; sökülürken <c>null</c> geçer. */
  registerApi?: (api: DocumentPaneApi | null) => void;
  isTr: boolean;
}

/** Her sayfanın gerçek en-boy oranı (yükseklik / genişlik). */
function usePageAspects(pdfDocument: PdfDocumentProxy | null, pageCount: number): number[] {
  const [aspects, setAspects] = useState<number[]>([]);

  useEffect((): (() => void) => {
    let cancelled = false;
    // Doküman yoksa setState ile temizlemiyoruz: senkron bir setState, effect'in içinde
    // ikinci bir render turu başlatır ve gereksizdir. Değer aşağıda TÜRETİLİYOR.
    if (!pdfDocument) return (): void => undefined;

    const read = async (): Promise<void> => {
      const out: number[] = [];
      const limit: number = Math.min(pdfDocument.numPages, Math.max(pageCount, 1));
      for (let page = 1; page <= limit; page++) {
        try {
          const proxy = await pdfDocument.getPage(page);
          const viewport = proxy.getViewport({ scale: 1 });
          out.push(viewport.width > 0 ? viewport.height / viewport.width : FALLBACK_PAGE_ASPECT);
        } catch {
          // Tek bir sayfanın oranı okunamazsa varsayılana düşüyoruz; kalan sayfalar yine
          // gerçek oranını alıyor. Tamamen vazgeçmek düzeltilen hatayı geri getirirdi.
          out.push(FALLBACK_PAGE_ASPECT);
        }
        if (cancelled) return;
      }
      if (!cancelled) setAspects(out);
    };

    void read();
    return (): void => {
      cancelled = true;
    };
  }, [pdfDocument, pageCount]);

  // Doküman yokken oran listesi anlamsız: state'i temizlemek yerine burada türetiliyor.
  // Böylece doküman değiştiğinde eski belgenin oranları bir kare boyunca kullanılmıyor.
  return pdfDocument ? aspects : [];
}

interface PageRange {
  from: number;
  to: number;
}

export function DocumentPane(props: DocumentPaneProps): JSX.Element {
  const {
    document: pdfDocument,
    pageCount,
    highlightsFor,
    label,
    pageWidth,
    onScrollPosition,
    registerApi,
    isTr
  } = props;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map<number, HTMLDivElement>());
  const [visiblePage, setVisiblePage] = useState<number>(1);
  const [range, setRange] = useState<PageRange>({ from: 1, to: 1 });
  const aspects: number[] = usePageAspects(pdfDocument, pageCount);

  /** Kare başına tek yazım için bekleyen hedef. */
  const pendingTarget = useRef<DocumentPosition | null>(null);
  const frame = useRef<number | null>(null);

  /**
   * Görünür sayfayı ve ÇİZİLECEK ARALIĞI kaydırma konumundan hesaplar.
   *
   * Görünür sayfa ölçütü kapsayıcının ORTASI: sayfa sınırında duran bir kaydırmada
   * "hangi sayfadayım" sorusunun tek kararlı cevabı bu. Çizilecek aralık ise kesişim
   * testi: görünür kutuyla (artı bir ekran boyu pay) kesişen her sayfa çiziliyor.
   */
  const recompute = useCallback((): void => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollTop: number = container.scrollTop;
    const height: number = container.clientHeight;
    const middle: number = scrollTop + height / 2;
    const overscan: number = height * OVERSCAN_RATIO;

    let current = 1;
    let currentElement: HTMLDivElement | null = null;
    // GÖRÜNÜR aralık ve ÖN YÜKLEME aralığı ayrı hesaplanıyor: bütçe yalnızca ikincisini
    // kısabilir. Tek bir aralık tutulduğu sürümde bütçe kuyruğu kesiyor ve ekranın
    // altındaki sayfalar yer tutucu kalıyordu.
    let visibleFrom = Number.MAX_SAFE_INTEGER;
    let visibleTo = 0;
    let overscanFrom = Number.MAX_SAFE_INTEGER;
    let overscanTo = 0;

    for (const [page, element] of pageRefs.current) {
      const top: number = element.offsetTop;
      const bottom: number = top + (element.clientHeight || 0);

      if (top <= middle && page >= current) {
        current = page;
        currentElement = element;
      }
      if (bottom >= scrollTop && top <= scrollTop + height) {
        if (page < visibleFrom) visibleFrom = page;
        if (page > visibleTo) visibleTo = page;
      }
      if (bottom >= scrollTop - overscan && top <= scrollTop + height + overscan) {
        if (page < overscanFrom) overscanFrom = page;
        if (page > overscanTo) overscanTo = page;
      }
    }

    if (visibleTo === 0) {
      visibleFrom = 1;
      visibleTo = 1;
    }
    if (overscanTo === 0) {
      overscanFrom = visibleFrom;
      overscanTo = visibleTo;
    }

    // Görünür aralık dokunulmaz. Kalan bütçe ön yükleme payına, iki yana dengeli dağıtılıyor.
    let from = visibleFrom;
    let to = visibleTo;
    let budget: number = MAX_RENDERED_PAGES - (visibleTo - visibleFrom + 1);
    while (budget > 0 && (from > overscanFrom || to < overscanTo)) {
      if (to < overscanTo) {
        to++;
        budget--;
      }
      if (budget > 0 && from > overscanFrom) {
        from--;
        budget--;
      }
    }

    setVisiblePage(current);
    setRange((prev: PageRange): PageRange => (prev.from === from && prev.to === to ? prev : { from, to }));

    if (onScrollPosition && currentElement) {
      const pageHeight: number = currentElement.clientHeight || 1;
      const offset: number = (scrollTop - currentElement.offsetTop) / pageHeight;
      onScrollPosition({ page: current, offset: Math.max(0, Math.min(1, offset)) });
    }
  }, [onScrollPosition]);

  const applyPending = useCallback((): void => {
    frame.current = null;
    const container = scrollRef.current;
    const target = pendingTarget.current;
    pendingTarget.current = null;
    if (!container || !target) return;
    const element = pageRefs.current.get(target.page);
    if (!element) return;
    container.scrollTop = element.offsetTop + target.offset * (element.clientHeight || 0);
    setVisiblePage(target.page);
    recompute();
  }, [recompute]);

  // Tutamağı ebeveyne ver. `useLayoutEffect`: ebeveynin ilk senkron denemesi boyamadan
  // önce gelebiliyor ve tutamak o an hazır olmalı.
  useLayoutEffect((): (() => void) => {
    if (!registerApi) return (): void => undefined;

    const api: DocumentPaneApi = {
      scrollToPosition: (position: DocumentPosition): void => {
        pendingTarget.current = position;
        if (frame.current !== null) return;
        frame.current = window.requestAnimationFrame(applyPending);
      },
      scrollToPage: (page: number): void => {
        const element = pageRefs.current.get(page);
        if (element) element.scrollIntoView({ block: "center", behavior: "smooth" });
        setVisiblePage(page);
      }
    };

    registerApi(api);
    return (): void => {
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
      frame.current = null;
      registerApi(null);
    };
  }, [registerApi, applyPending]);

  useEffect((): (() => void) => {
    const container = scrollRef.current;
    if (!container) return (): void => undefined;
    container.addEventListener("scroll", recompute, { passive: true });
    recompute();
    return (): void => container.removeEventListener("scroll", recompute);
  }, [recompute]);

  /*
    Kaydırma DIŞINDA da yeniden hesaplamak şart: zoom ve oranların gelmesi sayfa
    yüksekliklerini değiştiriyor ama hiçbir `scroll` olayı doğurmuyor. Bu olmadan
    zoom sonrası görünür sayfalar yer tutucu olarak kalırdı — düzeltilen hatanın
    tam olarak ikinci hâli.
  */
  useEffect((): void => {
    recompute();
  }, [pageWidth, aspects, pageCount, recompute]);

  const pages: number[] = [];
  for (let page = 1; page <= Math.max(pageCount, 0); page++) pages.push(page);

  const heightOf = (page: number): number => Math.round(pageWidth * (aspects[page - 1] ?? FALLBACK_PAGE_ASPECT));

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
      <div
        style={{
          height: 28,
          minHeight: 28,
          padding: "0 10px",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          color: "var(--kuika-cmp-text)",
          background: "var(--kuika-cmp-bg)",
          borderBottom: "1px solid var(--kuika-cmp-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0
        }}
      >
        <span>{label}</span>
        <span style={{ color: "var(--kuika-cmp-dim)", fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
          {isTr ? "sf" : "p"} {visiblePage}/{Math.max(pageCount, 1)}
        </span>
      </div>

      <div
        ref={scrollRef}
        data-testid={`document-pane-${label}`}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          background: "var(--kuika-cmp-surface)",
          padding: PAGE_PADDING,
          // Kaydırma kapsayıcısı aynı zamanda konumlandıran öğe: `offsetTop` ile
          // `scrollTop` aynı başlangıç noktasını paylaşmak zorunda. Aksi hâlde
          // `offsetTop` daha yukarıdaki bir atadan ölçülür ve sabit bir kayma oluşur.
          position: "relative"
        }}
      >
        {!pdfDocument && (
          <div style={{ color: "var(--kuika-cmp-dim)", padding: 14 }}>
            {isTr ? "Sayfa görüntüsü yüklenmedi." : "Page image not loaded."}
          </div>
        )}

        {pdfDocument &&
          pages.map((page: number) => {
            const rendered: boolean = page >= range.from && page <= range.to;
            return (
              <div
                key={page}
                // Her sayfanın kendi yuvası var ve numarasını taşıyor. Sürekli akışın
                // sözleşmesi bu: sayfa çizilmemiş olabilir (yer tutucu) ama YERİ her zaman
                // ayrılmış — düzenin oynamaması eşzamanlı kaydırmanın ön koşulu.
                data-page={page}
                data-rendered={rendered ? "true" : "false"}
                ref={(element: HTMLDivElement | null): void => {
                  if (element) pageRefs.current.set(page, element);
                  else pageRefs.current.delete(page);
                }}
                style={{
                  width: pageWidth,
                  // Yükseklik HER ZAMAN sabit — canvas çizilse de yer tutucu dursa da aynı.
                  // Düzenin oynamaması eşzamanlı kaydırmanın ön koşulu.
                  height: heightOf(page),
                  margin: `0 auto ${PAGE_GAP}px`,
                  background: "#fff",
                  boxShadow: "0 1px 6px rgba(0,0,0,0.28)",
                  overflow: "hidden"
                }}
              >
                {rendered ? (
                  <PdfPageCanvas
                    document={pdfDocument}
                    page={page}
                    highlights={highlightsFor(page)}
                    width={pageWidth}
                    isTr={isTr}
                  />
                ) : (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--kuika-cmp-dim)",
                      fontSize: 12
                    }}
                  >
                    {isTr ? "sf" : "p"} {page}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
